import { Badge, Button, Card, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ConnectorResponse } from "@/api";
import {
  clearMyConnectorCredentialMutation,
  errorMessage,
  getConnectorDetailQueryKey,
  listMyConnectorConnectionsQueryKey,
} from "@/api";
import { CopyValue } from "@/components/CopyValue";
import { Text } from "@/components/Text";
import { useConnectorSignIn } from "./useConnectorSignIn";

/**
 * An OAuth connector (phase 6): each person signs in to the server as themselves.
 * Admins also see the address to register with the provider.
 */
export function ConnectorSignInCard({ connector }: Readonly<{ connector: ConnectorResponse }>) {
  const { t } = useTranslation("connectors");
  const toast = useToast();
  const queryClient = useQueryClient();
  const { signIn, pendingUuid } = useConnectorSignIn();
  const signOut = useMutation(clearMyConnectorCredentialMutation());
  const path = { connector_uuid: connector.uuid };
  const signedIn = connector.hasMyCredential;
  const isAdmin = connector.currentUserRole === "admin";
  const ready = connector.signInReady;

  const remove = () =>
    signOut.mutate(
      { path },
      {
        onSuccess: async () => {
          await Promise.all(
            [getConnectorDetailQueryKey({ path }), listMyConnectorConnectionsQueryKey()].map((queryKey) =>
              queryClient.invalidateQueries({ queryKey }),
            ),
          );
          toast.success(t("signIn.signedOut", { name: connector.name }));
        },
        onError: (error) => toast.error(errorMessage(error, t("credentials.removeFailed"))),
      },
    );

  let status = t("signIn.notSignedIn");
  if (signedIn)
    status = connector.myAccount ? t("signIn.signedInAs", { account: connector.myAccount }) : t("signIn.signedIn");

  return (
    <Card>
      <section aria-label={t("signIn.title")} className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <LogIn size={16} aria-hidden className="text-content-secondary" />
          <Text variant="body">{t("signIn.title")}</Text>
          <Badge variant={signedIn ? "success" : "warning"}>{status}</Badge>
        </div>
        <Text variant="body-sm" tone="secondary">
          {t("signIn.body", { name: connector.name })}
        </Text>
        {!ready && (
          <Text variant="caption" tone="tertiary">
            {isAdmin ? t("signIn.notReadyAdmin") : t("signIn.notReady")}
          </Text>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={signedIn ? "secondary" : "primary"}
            size="sm"
            disabled={!ready}
            loading={pendingUuid === connector.uuid}
            onClick={() => signIn(connector.uuid)}
          >
            {signedIn ? t("signIn.again") : t("signIn.action", { name: connector.name })}
          </Button>
          {signedIn && (
            <Button variant="ghost" size="sm" loading={signOut.isPending} onClick={remove}>
              {t("signIn.signOut")}
            </Button>
          )}
        </div>
        {isAdmin && !connector.isOrg && connector.oauthRedirectUri && (
          <div className="flex flex-col gap-1 border-t border-border-subtle pt-3">
            <Text variant="caption" tone="secondary">
              {t("signIn.redirectUri")}
            </Text>
            <CopyValue value={connector.oauthRedirectUri} label={t("signIn.redirectUriLabel")} />
          </div>
        )}
      </section>
    </Card>
  );
}
